import { LVGLSchemaService, PropertySchema } from './LVGLSchemaService';
import { CoreTextModel, CorePosition, getValidStyles, stripXmlCommentsPreserveLines } from './common';
import {
    collectDocumentApiPropsFromDocument,
    getAnimationTargetNames,
    getTimelineNamesForTarget,
    resolveAllowedChildrenParentName
} from './validationHelpers';
import { getKnownEventCallbackNames, getCachedScreenNames } from './XMLValidatorCore';

export type CoreCompletionItemKind = 'class' | 'enumMember' | 'property' | 'color' | 'value';
export type CoreCompletionItemInsertTextRule = 'none' | 'keepWhitespace' | 'insertAsSnippet';

// Types here are modified from Monaco API:
export type CoreRange = {
    readonly startLineNumber: number;
    readonly startColumn: number;
    readonly endLineNumber: number;
    readonly endColumn: number;
};

export type CoreCommand = {
    id: string;
    title: string;
};

export type CoreCompletionItem = {
    label: string;
    kind: CoreCompletionItemKind;
    detail?: string;
    sortText?: string;
    insertText: string;
    insertTextRules?: CoreCompletionItemInsertTextRule;
    range: CoreRange;
    command?: CoreCommand;
};

export type CoreCompletionList = {
    suggestions: CoreCompletionItem[];
    incomplete?: boolean;
    dispose?(): void;
};

export const TRIGGER_CHARACTERS: string[] = ['<', ' ', '=', '"', '#', '$', '|', '{'];

// Strips a trailing array suffix (e.g. 'image[4]' -> 'image', 'font[]' -> 'font')
// so array-typed properties can reuse the same value suggestions as their base type.
const stripArraySuffix = (type: string | undefined): string | undefined =>
    type === undefined ? undefined : type.replace(/\[[^\]]*\]$/, '');

export const getWordRange = (model: CoreTextModel, position: CorePosition): CoreRange => {
    const lineContent: string = model.getLineContent(position.lineNumber);
    const textUntilPosition: string = lineContent.substring(0, Math.max(0, position.column - 1));
    const wordUntilPosition: string = textUntilPosition.match(/[#$\w_-]*$/)?.[0] || '';

    return {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: position.column - wordUntilPosition.length,
        endColumn: position.column
    };
};

const parsePipeSeparatedValues = (value: string): string[] =>
    Array.from(
        new Set(
            value
                .split('|')
                .map((part: string) => part.trim())
                .filter(Boolean)
        )
    );

const createValueSuggestions = (
    values: string[],
    range: CoreRange,
    detail?: (value: string) => string
): CoreCompletionItem[] =>
    values.map((value: string, index: number) => ({
        label: value,
        kind: 'value' as CoreCompletionItemKind,
        insertText: value,
        detail: detail?.(value),
        range,
        sortText: String(index).padStart(5, '0')
    }));

const SCREEN_VALUE_PROP_NAMES: ReadonlySet<string> = new Set<string>(['screen', 'target_screen']);

const getScreenNameSuggestions = (schemaService: LVGLSchemaService): string[] =>
    Array.from(new Set([...schemaService.getScreenNames(), ...getCachedScreenNames()])).sort((a, b) =>
        a.localeCompare(b)
    );

// Identifiers the runtime evaluator can resolve inside a `{ ... }` expression: consts, the
// document's API props (params), images, and fonts. Each is suggested bare (no `#`/`$` prefix)
// and tagged with its kind via `detail`. Subjects are intentionally omitted — the evaluator does
// not resolve them yet.
const buildExpressionIdentifierSuggestions = (
    schemaService: LVGLSchemaService,
    documentText: string,
    currentFilePath: string | undefined,
    range: CoreRange
): CoreCompletionItem[] => {
    const localConsts: string[] = Array.from(schemaService.consts.values())
        .filter((def) => def.filePath && def.filePath === currentFilePath)
        .map((def) => def.name);
    const consts: string[] = Array.from(new Set([...localConsts, ...schemaService.globalConsts.keys()]));
    const props: string[] = collectDocumentApiPropsFromDocument(documentText);

    const seen: Set<string> = new Set();
    const suggestions: CoreCompletionItem[] = [];
    // `category` groups kinds in the dropdown (consts first, fonts last); names sort within a group.
    const addAll = (names: string[], detail: string, category: number): void => {
        for (const name of [...names].sort((a, b) => a.localeCompare(b))) {
            if (seen.has(name)) continue;
            seen.add(name);
            suggestions.push({
                label: name,
                kind: 'value' as CoreCompletionItemKind,
                insertText: name,
                detail,
                range,
                sortText: `${category}${name}`
            });
        }
    };

    addAll(consts, 'const', 0);
    addAll(props, 'prop', 1);
    addAll(schemaService.images, 'image', 2);
    addAll(schemaService.fonts, 'font', 3);

    return suggestions;
};

export const provideCompletionItems = (
    schemaService: LVGLSchemaService,
    model: CoreTextModel,
    position: CorePosition
): CoreCompletionList => {
    const getSubjectSuggestions = (subjectType?: string): CoreCompletionItem[] => {
        const normalizedType: string | undefined = subjectType?.startsWith('subject:')
            ? subjectType.slice('subject:'.length)
            : undefined;
        const subjects: string[] = normalizedType
            ? Array.from(schemaService.subjectsByType?.[normalizedType] ?? []).sort((a, b) => a.localeCompare(b))
            : schemaService.subjects;

        return subjects.map((subject) => ({
            label: subject,
            kind: 'value' as CoreCompletionItemKind,
            insertText: subject,
            range: wordRange
        }));
    };

    // Create range for the current word
    const wordRange: CoreRange = getWordRange(model, position);

    // Get all text before current position to handle multi-line elements
    const textUntilCursor: string = model.getValue().substring(0, model.getOffsetAt(position));

    // Remove XML comments before processing
    const textWithoutComments: string = stripXmlCommentsPreserveLines(textUntilCursor);

    // Get all elements before current cursor position to find parent elements
    type ParentEntry = { name: string; extendsValue: string };

    const elements: RegExpExecArray[] = Array.from(textWithoutComments.matchAll(/<\/?([a-zA-Z][\w-]*)([\s\S]*?)>/g));
    const parentStack: ParentEntry[] = [];
    const fileBaseName: string = (model.uri.path.split(/[/\\]/).pop() ?? '').replace(/\.[^/.]+$/, '');

    for (const element of elements) {
        const isClosing: boolean = element?.[0]?.startsWith('</');
        const elementName: string = element?.[1];

        if (isClosing) parentStack.pop();
        else if (!element[0].endsWith('/>')) {
            const extendsValue: string = element[0].match(/extends=["']([^"']*)["']/)?.[1] ?? '';
            parentStack.push({ name: elementName, extendsValue });
        }
    }

    // Get the immediate parent element
    const parentEntry: ParentEntry | undefined = parentStack[parentStack.length - 1];
    const parentElement: string = parentEntry?.name;
    const parentExtends: string = parentEntry?.extendsValue || '';
    const rootElement: string | undefined = parentStack[0]?.name;
    const childrenAllowAttrMatch: RegExpMatchArray | null = textWithoutComments.match(
        /<children\b[^>]*\sallow=["']([^"']*)$/
    );
    if (childrenAllowAttrMatch && (rootElement === 'component' || rootElement === 'widget')) {
        const typedPrefix: string = childrenAllowAttrMatch[1];
        const trailingPartial: string = typedPrefix.match(/(\S*)$/)?.[1] ?? '';
        const completedPrefix: string = typedPrefix.slice(0, typedPrefix.length - trailingPartial.length).trimEnd();
        const existingTokens: Set<string> = new Set(
            completedPrefix.length ? completedPrefix.split(/\s+/).filter(Boolean) : []
        );
        const needsLeadingSpace: boolean =
            existingTokens.size > 0 && trailingPartial.length === 0 && !/\s$/.test(typedPrefix);

        const allowTokenRange: CoreRange = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: position.column - trailingPartial.length,
            endColumn: position.column
        };

        const ownerSchema = schemaService.getElement(fileBaseName);
        const suggestionSet: Set<string> = new Set<string>(['events']);
        const ownerPrefix: string = `${fileBaseName}-`;
        ownerSchema?.elements?.forEach((fullName: string) => {
            suggestionSet.add(fullName.startsWith(ownerPrefix) ? fullName.slice(ownerPrefix.length) : fullName);
        });
        schemaService.getAllowedChildrenForElement(ownerSchema?.extends || 'lv_obj').forEach((childName: string) => {
            suggestionSet.add(childName);
            if (childName.startsWith('lv_obj-')) suggestionSet.add(childName.replace(/^lv_obj-/, ''));
        });
        const suggestions: string[] = Array.from(suggestionSet).filter(
            (token) => !existingTokens.has(token) && token.startsWith(trailingPartial) && token !== trailingPartial
        );

        if (suggestions.length) {
            return {
                suggestions: suggestions.map((token, index) => ({
                    label: token,
                    kind: 'value',
                    insertText: `${needsLeadingSpace ? ' ' : ''}${token}`,
                    range: allowTokenRange,
                    sortText: String(index).padStart(5, '0')
                }))
            };
        }
    }

    // Suggest elements when typing '<'
    if (textWithoutComments.endsWith('<')) {
        // Get the file name from the model URI
        const fileName: string | undefined = model.uri.path.split('/').pop()?.toLowerCase();

        // Special handling for root elements based on file name
        if (!parentElement && fileName) {
            const rootElementSuggestions: CoreCompletionItem[] = [];

            // Add file-specific root element suggestions
            const createRootSuggestion = (label: string) => ({
                label,
                kind: 'class' as CoreCompletionItemKind,
                insertText: `${label}>\n    <$0\n</${label}>`,
                insertTextRules: 'insertAsSnippet' as CoreCompletionItemInsertTextRule,
                range: wordRange,
                command: {
                    id: 'editor.action.triggerSuggest',
                    title: 'Trigger Suggest'
                }
            });

            if (fileName === 'globals.xml') rootElementSuggestions.push(createRootSuggestion('globals'));
            else if (fileName === 'project.xml') rootElementSuggestions.push(createRootSuggestion('project'));
            else if (fileName.endsWith('.xml'))
                rootElementSuggestions.push(
                    ...['component', 'screen', 'widget', 'test', 'translations'].map(createRootSuggestion)
                );

            // If we have file-specific suggestions, return them
            if (rootElementSuggestions?.length) return { suggestions: rootElementSuggestions };
        }

        const allowedChildrenParent: string = parentElement
            ? resolveAllowedChildrenParentName(
                  schemaService,
                  parentEntry ? { name: parentEntry.name, extends: parentEntry.extendsValue } : undefined,
                  fileBaseName
              )
            : '';
        const allowedChildren: string[] = schemaService
            .getAllowedChildrenForElement(allowedChildrenParent || parentExtends || parentElement)
            .filter((child) => !(rootElement === 'component' && parentElement === 'prop' && child === 'param'))
            .filter((child) => !(rootElement !== 'component' && parentElement === 'api' && child === 'slot'));

        // Insert text based on the element attributes and allowed children
        const insertText = (elementName: string): string => {
            const shouldAddNameProp: boolean = ['style', 'param', 'prop'].includes(elementName);
            if (schemaService.hasAllowedChildren(elementName) && schemaService.hasElementProperties(elementName))
                return `${elementName} ${shouldAddNameProp ? 'name="$0"' : '$0'}></${elementName}>`;
            else if (schemaService.hasAllowedChildren(elementName))
                return `${elementName}>\n    <$0\n</${elementName}>`;
            else return `${elementName} ${shouldAddNameProp ? 'name="$0"' : '$0'} />`;
        };

        // Add allowed children
        if (allowedChildren?.length) {
            // Prefer lv_obj-* variants if available, but show both names
            const preferred: Map<string, string> = new Map<string, string>();
            for (const child of allowedChildren) {
                const key: string = child.replace(/^lv_obj-/, '');
                // If both lv_obj-* and non-lv_obj-* exist, prefer lv_obj-*
                if (!preferred.has(key) || child.startsWith('lv_obj-')) preferred.set(key, child);
            }

            // Build completion suggestions for allowed children
            const suggestions: CoreCompletionItem[] = Array.from(preferred.values()).map((child) => ({
                // Show both the display name and the full type if lv_obj-*
                label: child.startsWith('lv_obj-') ? `${child} (${child.replace(/^lv_obj-/, '')})` : child,
                kind: 'class',
                insertText: insertText(child.replace(/^lv_obj-/, '')),
                insertTextRules: 'insertAsSnippet',
                range: wordRange,
                command: { id: 'editor.action.triggerSuggest', title: 'Trigger Suggest' },
                sortText: 'a'
            }));

            // Sort suggestions alphabetically by label
            suggestions.sort((a, b) => a.label.localeCompare(b.label));

            return { suggestions };
        }
    }

    // Detect if inside an property value
    const propMatch: RegExpMatchArray | null = textWithoutComments.match(/(\w+)=["']([^"']*)?$/s);

    // Inside an unterminated `{ ... }` expression, consts/props/etc. are referenced by bare name,
    // not via `#`/`$`, so the prefix handlers below are disabled and the expression handler runs.
    const exprBraceIdx: number = (propMatch?.[2] ?? '').lastIndexOf('{');
    const insideBraceExpression: boolean =
        exprBraceIdx !== -1 && !(propMatch![2] ?? '').slice(exprBraceIdx).includes('}');

    // Suggest consts after '#' inside attribute values (but not inside <consts>)
    if (propMatch && !insideBraceExpression && !parentStack.some((entry) => entry.name === 'consts')) {
        const constMatch: RegExpMatchArray | null = textWithoutComments.match(/#([\w-]*)$/);
        if (constMatch) {
            const currentFilePath: string | undefined = model.uri?.path;
            const localConsts: string[] = Array.from(schemaService.consts.values())
                .filter((def) => def.filePath && def.filePath === currentFilePath)
                .map((def) => def.name);
            const globalConsts: string[] = Array.from(schemaService.globalConsts.keys());
            const constNames: string[] = Array.from(new Set([...localConsts, ...globalConsts])).sort((a, b) =>
                a.localeCompare(b)
            );
            return {
                suggestions: constNames.map((name, index) => ({
                    label: `#${name}`,
                    kind: 'value' as CoreCompletionItemKind,
                    insertText: `#${name}`,
                    range: wordRange,
                    sortText: String(index).padStart(5, '0'),
                    detail: schemaService.getConstDefinition(name)?.help
                }))
            };
        }
    }

    // Suggest props after '$' inside attribute values
    if (propMatch && !insideBraceExpression) {
        const propValue: string = propMatch[2] ?? '';
        const propRefMatch: RegExpMatchArray | null = propValue.match(/\$([\w-]*)$/);
        if (propRefMatch) {
            const propNames: string[] = [
                ...Array.from(schemaService.globalApiProps.keys()).sort((a, b) => a.localeCompare(b)),
                ...collectDocumentApiPropsFromDocument(textWithoutComments)
            ];
            return {
                suggestions: propNames.map((name, index) => ({
                    label: `$${name}`,
                    kind: 'value' as CoreCompletionItemKind,
                    insertText: `$${name}`,
                    range: wordRange,
                    sortText: String(index).padStart(5, '0')
                }))
            };
        }
    }

    // Suggest identifiers (consts, props, subjects, images, fonts) while typing inside a
    // `{ ... }` expression, e.g. `{const1 * 3 * prop1}`. Skip when the cursor is inside a string
    // literal, where a bare identifier isn't an operand.
    if (insideBraceExpression) {
        const exprText: string = (propMatch![2] ?? '').slice(exprBraceIdx + 1);
        const insideStringLiteral: boolean = (exprText.match(/'/g)?.length ?? 0) % 2 === 1;
        if (!insideStringLiteral) {
            const suggestions: CoreCompletionItem[] = buildExpressionIdentifierSuggestions(
                schemaService,
                textWithoutComments,
                model.uri?.path,
                wordRange
            );
            if (suggestions.length > 0) return { suggestions };
        }
    }

    // Find the last opening element that hasn't been closed
    const lastElementMatch: RegExpMatchArray | null = textWithoutComments.match(/<([a-zA-Z][\w-]*)(?:\s+[^>]*)?$/s);
    if (!lastElementMatch) return { suggestions: [] };

    const elementName: string = lastElementMatch[1];

    // Extract existing properties from the current element
    const existingProps: Set<string> = new Set<string>();
    let extendsValue: string = '';

    // Find all properties in the current element
    const elementProps: string = textWithoutComments.match(/<[^>]*$/)?.[0] || '';
    const propMatches: RegExpStringIterator<RegExpExecArray> = elementProps.matchAll(/(\w+)=["'][^"']*["']/g);

    for (const match of propMatches) {
        const propName: string = match[1];
        existingProps.add(propName);

        // Track extends value if present
        if (propName === 'extends') {
            const extendsMatch: RegExpMatchArray | null = elementProps.match(/extends=["']([^"']*)["']/);
            if (extendsMatch) extendsValue = extendsMatch[1];
        }
    }

    // Handle regular element properties
    if (!propMatch) {
        // Get base properties for the element
        const [ownProps, inheritedProps] = schemaService.getPropertyNames(
            elementName,
            extendsValue || undefined,
            parentElement
        );

        // Combine all properties and filter out existing ones
        const allProps: string[] = [...ownProps, ...inheritedProps];
        const uniqueProps: string[] = Array.from(new Set(allProps)).filter((prop) => !existingProps.has(prop));
        const filteredProps: string[] =
            parentElement === 'consts' && elementName === 'int'
                ? uniqueProps.filter((prop) => prop !== 'min_value' && prop !== 'max_value')
                : uniqueProps;

        return {
            suggestions: filteredProps.map((prop, index) => ({
                label: prop,
                kind: 'property' as CoreCompletionItemKind,
                insertText: `${prop}="$0"`,
                insertTextRules: 'insertAsSnippet',
                detail: schemaService.getPropertyHelp(elementName, prop),
                range: wordRange,
                sortText: String(index).padStart(5, '0'),
                command:
                    schemaService.getPropertyType(prop, elementName)[0] === 'int'
                        ? undefined
                        : {
                              id: 'editor.action.triggerSuggest',
                              title: 'Trigger Suggest'
                          }
            }))
        };
    }

    // Handle enum values inside property value quotes
    if (propMatch) {
        const [, propName] = propMatch;

        // If prop is 'extends', suggest all the elements.
        if (propName === 'extends') {
            return {
                suggestions: Array.from(
                    Array.from(schemaService.elements.entries())
                        .filter(
                            ([_name, element]) =>
                                !['lvgl-element', 'element-child', 'lvgl-element-child'].includes(element.type)
                        )
                        .map(([key]) => key)
                ).map((element: string) => ({
                    label: element,
                    kind: 'class' as CoreCompletionItemKind,
                    insertText: element,
                    range: wordRange
                }))
            };
        }

        if (propName === 'translation_tag') {
            const translationTags: string[] = schemaService.getTranslationTags();
            if (translationTags.length > 0) {
                return {
                    suggestions: createValueSuggestions(translationTags, wordRange)
                };
            }
        }

        if ((elementName === 'images' || elementName === 'fonts') && propName === 'memory') {
            const targetAttrValue: string = elementProps.match(/\sif_target=["']([^"']*)["']/)?.[1] ?? '';
            const targetNames: string[] = parsePipeSeparatedValues(targetAttrValue);
            const memoryNames: string[] = schemaService.getProjectMemoryNames(targetNames);

            return {
                suggestions: createValueSuggestions(memoryNames, wordRange, () =>
                    targetNames.length > 0
                        ? `Memory region available for ${targetNames.join('|')}`
                        : 'Project memory region'
                )
            };
        }

        if (propName === 'type' && (elementName === 'prop' || elementName === 'param')) {
            const primitiveTypes: string[] = schemaService.getEnumValues('type');
            const enumTypes: string[] = [...schemaService.enums.keys()].map((name) => `enum:${name}`);
            return {
                suggestions: [...primitiveTypes, ...enumTypes].map((typeValue, index) => ({
                    label: typeValue,
                    kind: 'enumMember' as CoreCompletionItemKind,
                    insertText: typeValue,
                    range: wordRange,
                    sortText: String(index).padStart(5, '0')
                }))
            };
        }

        if (propName === 'default' && elementName === 'prop') {
            const propTypeFromElement = elementProps.match(/type=["']([^"']+)["']/)?.[1];

            if (propTypeFromElement === 'lv_event') {
                return {
                    suggestions: schemaService.getEnumValues('lv_event').map((event) => ({
                        label: event,
                        kind: 'enumMember' as CoreCompletionItemKind,
                        insertText: event,
                        detail: schemaService.getEnumHelp('lv_event', event) || '',
                        range: wordRange
                    }))
                };
            }

            if (propTypeFromElement === 'event_cb') {
                return {
                    suggestions: createValueSuggestions(getKnownEventCallbackNames(), wordRange, () => 'Event callback')
                };
            }

            if (propTypeFromElement === 'screen') {
                return {
                    suggestions: createValueSuggestions(
                        getScreenNameSuggestions(schemaService),
                        wordRange,
                        () => 'Screen'
                    )
                };
            }

            if (propTypeFromElement === 'subject') {
                return {
                    suggestions: schemaService.subjects.map((s) => ({
                        label: s,
                        kind: 'value' as CoreCompletionItemKind,
                        insertText: s,
                        range: wordRange
                    }))
                };
            }

            if (propTypeFromElement === 'color') {
                return {
                    suggestions: schemaService.colors.map(({ name, value }) => ({
                        label: value.replace('0x', '#'),
                        kind: 'color' as CoreCompletionItemKind,
                        insertText: value,
                        detail: name,
                        range: wordRange
                    }))
                };
            }
            if (stripArraySuffix(propTypeFromElement) === 'image') {
                return {
                    suggestions: schemaService.images.map((image) => ({
                        label: image,
                        kind: 'value' as CoreCompletionItemKind,
                        insertText: image,
                        range: wordRange
                    }))
                };
            }

            if (stripArraySuffix(propTypeFromElement) === 'font') {
                return {
                    suggestions: schemaService.fonts.map((font) => ({
                        label: font,
                        kind: 'value' as CoreCompletionItemKind,
                        insertText: font,
                        range: wordRange
                    }))
                };
            }

            if (
                propTypeFromElement?.startsWith('enum:') ||
                propTypeFromElement === 'selector+' ||
                propTypeFromElement === 'lv_event'
            ) {
                const enumType: string = propTypeFromElement.startsWith('enum:')
                    ? propTypeFromElement.split(':')[1]
                    : propTypeFromElement;

                const isMultiValue: boolean = enumType.endsWith('+');
                const baseEnumType: string = isMultiValue ? enumType.slice(0, -1) : enumType;

                const enumValues: string[] =
                    propTypeFromElement !== 'selector+'
                        ? schemaService.getEnumValues(baseEnumType)
                        : schemaService.getEnumValues('lv_state').concat(schemaService.getEnumValues('lv_part'));

                return {
                    suggestions: enumValues.map((value) => {
                        return {
                            label: value,
                            kind: 'enumMember' as CoreCompletionItemKind,
                            insertText: value,
                            detail: schemaService.getEnumHelp(baseEnumType, value) || '',
                            range: wordRange
                        };
                    })
                };
            }
        }

        // Get the type of the property
        let [propType] = schemaService.getPropertyType(elementName, propName, extendsValue, parentElement);

        // Screen args are stored on element args, not style properties — still offer screens when the
        // attribute name is unambiguous so Monaco does not fall back to unrelated words in the file.
        if (!propType && SCREEN_VALUE_PROP_NAMES.has(propName)) {
            propType = 'screen';
        }

        const prop: PropertySchema | undefined = schemaService
            .getElementProperties(elementName)
            .find((p) => p.name === propName);

        // If prop has values, suggest the values
        if (prop?.values) {
            return {
                suggestions: prop.values.map((value) => ({
                    label: value,
                    kind: 'value' as CoreCompletionItemKind,
                    insertText: value,
                    range: wordRange,
                    sortText: '0'
                }))
            };
        }

        if (propType === 'screen') {
            return {
                suggestions: createValueSuggestions(getScreenNameSuggestions(schemaService), wordRange, () => 'Screen')
            };
        }

        if (propType === 'event_cb') {
            return {
                suggestions: createValueSuggestions(getKnownEventCallbackNames(), wordRange, () => 'Event callback')
            };
        }

        // UI element references: named widgets in <view>, plus 'self' when the type
        // allows it (parse the full document, <view> usually comes after the cursor).
        if (propType === 'lv_obj' || propType === 'lv_obj|self') {
            const fullTextWithoutComments: string = stripXmlCommentsPreserveLines(model.getValue());
            return {
                suggestions: createValueSuggestions(
                    getAnimationTargetNames(fullTextWithoutComments, propType, schemaService),
                    wordRange,
                    (value) => (value === 'self' ? 'This component/screen' : 'Named UI element')
                )
            };
        }

        // IDK why but propType doesn't work for <images .../>
        if (propName === 'if_target' || propType?.startsWith('if_target')) {
            return {
                suggestions: createValueSuggestions(
                    [...schemaService.getProjectTargetNames()],
                    wordRange,
                    () => 'Project target'
                )
            };
        }

        // Timelines of the element's target ('self' = current document, otherwise the
        // target widget's component). The target attribute may be after the cursor,
        // so read it from the complete current tag.
        if (propType === 'timeline') {
            const fullText: string = model.getValue();
            const tagStart: number = textUntilCursor.lastIndexOf('<');
            const tagEnd: number = fullText.indexOf('>', tagStart);
            const currentTag: string = fullText.slice(tagStart, tagEnd === -1 ? undefined : tagEnd);
            const target: string | undefined = currentTag.match(/\btarget\s*=\s*["']([^"']*)["']/)?.[1];

            const timelineNames: string[] =
                getTimelineNamesForTarget(stripXmlCommentsPreserveLines(fullText), target, schemaService) ?? [];
            return {
                suggestions: createValueSuggestions(timelineNames, wordRange, () => 'Timeline')
            };
        }

        if (propType === undefined) {
            // Cannot suggest completions, no known property type
        }
        // If propType's type is a string and starts with 'enum:', suggest the enum values
        // (or it is any of the things that function like enums that do not have 'enum:' in the type)
        else if (propType.startsWith('enum:') || propType === 'selector+' || propType === 'lv_event') {
            const enumType: string = propType.split(':')[1] || propType;
            const isMultiValue: boolean = enumType.endsWith('+');
            const baseEnumType: string = isMultiValue ? enumType.slice(0, -1) : enumType;

            // Special case that acts like an enum
            const enumValues: string[] =
                propType !== 'selector+'
                    ? schemaService.getEnumValues(baseEnumType)
                    : schemaService.getEnumValues('lv_state').concat(schemaService.getEnumValues('lv_part'));

            return {
                suggestions: enumValues.map((value) => ({
                    label: value,
                    kind: 'enumMember' as CoreCompletionItemKind,
                    insertText: value,
                    detail: `${schemaService.getEnumHelp(baseEnumType, value) || ''}${
                        isMultiValue ? '\n\n*Multiple values allowed (separate with |)*' : ''
                    }`,
                    range: wordRange
                }))
            };
        } else if (propType === 'color') {
            // TODO: Get colors from project and elements
            // [Architectural Improvement]
            return {
                suggestions: schemaService.colors.map(({ name, value }) => ({
                    label: value.replace('0x', '#'),
                    kind: 'color' as CoreCompletionItemKind,
                    insertText: value,
                    detail: name,
                    range: wordRange
                }))
            };
        } else if (propType === 'bool') {
            return {
                suggestions: ['true', 'false'].map((v) => ({
                    label: v,
                    kind: 'bool' as CoreCompletionItemKind,
                    insertText: v,
                    range: wordRange
                }))
            };
        } else if (stripArraySuffix(propType) === 'font') {
            // Matches 'font' as well as array forms like 'font[]' / 'font[4]', so
            // fonts are suggested for each element of a font array too.
            return {
                suggestions: schemaService.fonts.map((font) => ({
                    label: font,
                    kind: 'value' as CoreCompletionItemKind,
                    insertText: font,
                    range: wordRange
                }))
            };
        } else if (stripArraySuffix(propType) === 'image') {
            // Matches 'image' as well as array forms like 'image[]' / 'image[4]'.
            return {
                suggestions: schemaService.images.map((image) => ({
                    label: image,
                    kind: 'value' as CoreCompletionItemKind,
                    insertText: image,
                    range: wordRange
                }))
            };
        } else if (propType === 'subject' || propType.startsWith('subject:')) {
            return {
                suggestions: getSubjectSuggestions(propType)
            };
        } else if (propType === 'style') {
            // Suggest local and global styles
            return {
                suggestions: getValidStyles(textWithoutComments, schemaService).map((style: string) => ({
                    label: style,
                    kind: 'value' as CoreCompletionItemKind,
                    insertText: style,
                    range: wordRange
                }))
            };
        } else if (propType === 'string:lvgl_version') {
            return {
                suggestions: LVGLSchemaService.LVGL_SCHEMA_VERSIONS.map((version: string) => ({
                    label: version.replace('v', ''),
                    kind: 'value' as CoreCompletionItemKind,
                    insertText: version.replace('v', ''),
                    range: wordRange
                }))
            };
        } else if (propType === 'style_prop') {
            // Intentionally not using schemaService.getElement because that will lookup the object add style
            const styleProps = schemaService.elements.get('style');

            return {
                suggestions: (styleProps?.properties || []).map((styleProp) => ({
                    label: styleProp.name,
                    kind: 'enumMember' as CoreCompletionItemKind,
                    insertText: styleProp.name,
                    range: wordRange
                }))
            };
        }
    }

    return { suggestions: [] };
};
